import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { HireJobContextValue, HireJobDraft } from './hireJobContextTypes';

const initialJob: HireJobDraft = {
  categoryId: null,
  subCategoryId: null,
  askingPrice: '',
  buyerCost: '',
  minimumCost: null,
  termsAccepted: false,
  description: '',
  vinOrSerial: '',
  year: '',
  make: '',
  model: '',
  listing: '',
  address: '',
  jobLatitude: null,
  jobLongitude: null,
  city: '',
  state: '',
  zip: '00000',
  contactType: 'PRIVATE',
  contactName: '',
  contactNumber: '',
  contactEmail: '',
  specialInstructions: '',
};

const HireJobContext = createContext<HireJobContextValue | null>(null);

export const HireJobProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [createJob, setCreateJob] = useState<HireJobDraft>(initialJob);

  const saveJob = useCallback((patch: Partial<HireJobDraft>) => {
    setCreateJob((current) => ({ ...current, ...patch }));
  }, []);

  const resetJob = useCallback(() => {
    setCreateJob(initialJob);
  }, []);

  const value = useMemo(
    () => ({ createJob, saveJob, resetJob }),
    [createJob, saveJob, resetJob]
  );

  return (
    <HireJobContext.Provider value={value}>{children}</HireJobContext.Provider>
  );
};

export const useHireJob = (): HireJobContextValue => {
  const value = useContext(HireJobContext);
  if (!value) {
    throw new Error('useHireJob must be used within HireJobProvider');
  }
  return value;
};
